import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import Chart from 'chart.js/auto';
import { ReactWordcloud } from '@cp949/react-wordcloud';
import { LoadingBar } from '../components/shared/Skeletons';
import InviteAdminModal from '../components/admins/InviteAdminModal';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────

const THEME_COLORS = {
  water:       { bg: 'rgba(84,122,132,0.1)',   color: '#2a9d8f', border: '#2a9d8f' },
  electricity: { bg: 'rgba(240,133,109,0.12)', color: '#f0856d', border: '#f0856d' },
  employment:  { bg: 'rgba(57,91,129,0.1)',    color: '#395b81', border: '#395b81' },
  housing:     { bg: 'rgba(106,60,120,0.1)',   color: '#6a3c78', border: '#6a3c78' },
  health:      { bg: 'rgba(42,157,143,0.1)',   color: '#38b2ac', border: '#38b2ac' },
  safety:      { bg: 'rgba(230,57,70,0.1)',    color: '#e63946', border: '#e63946' },
  other:       { bg: 'rgba(56,57,78,0.07)',    color: '#e9c46a', border: '#e9c46a' },
};

// Vivid word cloud palette — one colour per theme, visually distinct
// Backend design palette — used for all data viz (word cloud, chart lines, marimekko columns)
const WORDCLOUD_PALETTE = [
  '#b7d12a', // lime green
  '#2aa9b0', // teal
  '#805ad5', // purple
  '#d9467a', // pink
  '#dd6b20', // orange
];

function getWordcloudColor(word, words) {
  const idx = words.findIndex(w => w.text === word.text);
  return WORDCLOUD_PALETTE[idx % WORDCLOUD_PALETTE.length];
}

const CHANNEL_COLORS = {
  whatsapp: '#547a84',
  sms: '#395b81',
  email: '#38394e',
  web: '#f0856d',
};

const STATUS_COLORS = {
  replied: '#547a84',
  reviewed: '#395b81',
  new: '#f0856d',
  failed: '#c06a55',
};

const getThemeColor = (theme) =>
  THEME_COLORS[theme?.toLowerCase()] || THEME_COLORS.other;

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function formatResponseTime(mins) {
  if (mins === null || mins === undefined) return '—';
  if (mins < 60) return `${Math.round(mins)}m`;
  return `${(mins / 60).toFixed(1)}h`;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function periodKey(id, granularity) {
  if (granularity === 'hourly') return `${id.year}-${String(id.month).padStart(2, '0')}-${String(id.day).padStart(2, '0')} ${String(id.hour).padStart(2, '0')}:00`;
  if (granularity === 'weekly') return `${id.year}-W${String(id.week).padStart(2, '0')}`;
  if (granularity === 'monthly') return `${id.year}-${String(id.month).padStart(2, '0')}`;
  return `${id.year}-${String(id.month).padStart(2, '0')}-${String(id.day).padStart(2, '0')}`;
}

function formatPeriodKey(k, granularity) {
  if (granularity === 'weekly') return k;
  if (granularity === 'hourly') return k.split(' ')[1];
  if (granularity === 'monthly') {
    const [, m] = k.split('-');
    return MONTH_NAMES[parseInt(m) - 1];
  }
  const parts = k.split('-');
  return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
}

// Builds chart data where each dataset is a topic line, Y = avg sentiment score (-2 to +2)
// activeTheme: if set, only that topic is shown; otherwise top 5 by message count in current window
function buildChartData(sentimentOverTime, granularity, activeTheme) {
  if (!sentimentOverTime?.length) return { labels: [], datasets: [] };

  let visibleTopics;
  if (activeTheme) {
    visibleTopics = [activeTheme];
  } else {
    const topicCounts = {};
    sentimentOverTime.forEach(({ _id, count }) => {
      topicCounts[_id.theme] = (topicCounts[_id.theme] || 0) + count;
    });
    visibleTopics = Object.entries(topicCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([topic]) => topic);
  }

  // topic -> periodKey -> avgScore
  const topicPeriods = {};
  const allPeriodKeys = new Set();

  sentimentOverTime.forEach(({ _id, avgScore }) => {
    const topic = _id.theme;
    if (!visibleTopics.includes(topic)) return;
    const key = periodKey(_id, granularity);
    allPeriodKeys.add(key);
    if (!topicPeriods[topic]) topicPeriods[topic] = {};
    topicPeriods[topic][key] = avgScore;
  });

  const sortedKeys = [...allPeriodKeys].sort();
  const labels = sortedKeys.map(k => formatPeriodKey(k, granularity));

  const filteredTopics = visibleTopics.filter(topic => topicPeriods[topic]);

  const datasets = filteredTopics.map((topic, idx) => {
      const color = WORDCLOUD_PALETTE[idx % WORDCLOUD_PALETTE.length];
      return {
        label: topic.charAt(0).toUpperCase() + topic.slice(1),
        data: sortedKeys.map(k => {
          const v = topicPeriods[topic]?.[k];
          return v != null ? Math.round(v * 10) / 10 : null;
        }),
        borderColor: color,
        backgroundColor: color + '20',
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.35,
        fill: false,
        spanGaps: true,
      };
    });

  return { labels, datasets };
}

// ─── SHARED UI ───────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <p style={{
      fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: '#888780', margin: 0,
    }}>
      {children}
    </p>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background: 'var(--card, #fff)',
      border: '1px solid var(--border, #e5e7eb)',
      borderRadius: 10,
      padding: '16px 20px',
      ...style,
    }}>
      {children}
    </div>
  );
}

function CardTitle({ children }) {
  return (
    <p style={{
      fontSize: 11, fontWeight: 600, color: '#888780',
      marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>
      {children}
    </p>
  );
}

function StatRow({ label, value, valueColor }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '6px 0', borderBottom: '1px solid var(--border, #f0f0f0)', fontSize: 13,
    }}>
      <span style={{ color: 'var(--muted-foreground, #6b7280)' }}>{label}</span>
      <span style={{ fontWeight: 600, color: valueColor || 'var(--foreground, #111)' }}>{value}</span>
    </div>
  );
}

function Skeleton({ height = 160 }) {
  return (
    <div style={{
      background: 'var(--accent, #f3f4f6)',
      borderRadius: 10,
      height,
    }} />
  );
}

function EmptyState({ message = 'No data yet' }) {
  return (
    <p style={{ fontSize: 13, color: '#9ca3af', padding: '8px 0' }}>{message}</p>
  );
}

// ─── WORD CLOUD ───────────────────────────────────────────────────────────────

// Defined outside component so references stay stable and don't trigger re-renders
const WORDCLOUD_OPTIONS = {
  rotations: 1,
  rotationAngles: [0, 0],
  fontSizes: [13, 38],
  padding: 4,
  fontFamily: 'inherit',
  enableTooltip: true,
  deterministic: true,
  scale: 'sqrt',
  spiral: 'rectangular',
};

function WordCloud({ themeCounts, activeTheme, onThemeClick }) {
  const words = useMemo(
    () => (themeCounts || []).map(({ theme, count }) => ({ text: theme, value: count })),
    [themeCounts]
  );

  const callbacks = useMemo(() => ({
    onWordClick: (word) => onThemeClick(word.text),
    getWordColor: (word) => {
      const base = getWordcloudColor(word, words);
      if (activeTheme && activeTheme !== word.text) return base + '40';
      return base;
    },
    getWordTooltip: (word) => `${word.text}: ${word.value} messages`,
  }), [activeTheme, onThemeClick, words]);

  if (!words.length) {
    return (
      <div style={{
        background: 'var(--accent, #f9fafb)', border: '1px solid var(--border, #e5e7eb)',
        borderRadius: 10, padding: 32, textAlign: 'center',
        color: 'var(--muted-foreground, #9ca3af)', fontSize: 13,
      }}>
        No theme data yet
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--accent, #f9fafb)',
      border: '1px solid var(--border, #e5e7eb)',
      borderRadius: 10,
      height: '100%',
      minHeight: 320,
      cursor: 'pointer',
      overflow: 'hidden',
    }}>
      <ReactWordcloud
        words={words}
        callbacks={callbacks}
        options={WORDCLOUD_OPTIONS}
      />
    </div>
  );
}

// ─── MARIMEKKO CHART ──────────────────────────────────────────────────────────

// sentimentBreakdown: [{ theme, positive, neutral, negative, total }]
// If not available, derives from themeCounts + sentimentOverTime
function MarimekkoChart({ themeCounts, sentimentBreakdown, activeTheme, onThemeClick }) {
  const [tooltip, setTooltip] = useState(null);

  const SENT_COLORS = {
    positive: '#2e8b57',
    neutral: '#ffc83c',
    negative: '#C53030',
  };

  const themes = useMemo(() => {
    if (!themeCounts?.length) return [];
    const sorted = [...themeCounts].sort((a, b) => b.count - a.count);
    return sorted.map(({ theme, count }) => {
      const bd = sentimentBreakdown?.find(s => s.theme === theme);
      const pos = bd ? bd.positive : Math.round(count * 0.33);
      const neg = bd ? bd.negative : Math.round(count * 0.33);
      const neu = bd ? bd.neutral : count - pos - neg;
      return { theme, count, pos, neu, neg };
    });
  }, [themeCounts, sentimentBreakdown]);

  const grandTotal = themes.reduce((s, t) => s + t.count, 0);

  if (!themeCounts?.length) {
    return (
      <div style={{
        background: 'var(--accent, #f9fafb)', border: '1px solid var(--border, #e5e7eb)',
        borderRadius: 10, padding: 32, textAlign: 'center',
        color: 'var(--muted-foreground, #9ca3af)', fontSize: 13, height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        No theme data yet
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--accent, #f9fafb)',
      border: '1px solid var(--border, #e5e7eb)',
      borderRadius: 10,
      padding: '16px 16px 12px',
      height: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
        {['positive', 'neutral', 'negative'].map(s => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: SENT_COLORS[s] }} />
            <span style={{ color: '#6b7280', textTransform: 'capitalize' }}>{s}</span>
          </div>
        ))}
      </div>

      {/* Chart area */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 3, minHeight: 220, position: 'relative' }}>
        {themes.map(({ theme, count, pos, neu, neg }) => {
          const widthPct = (count / grandTotal) * 100;
          const isActive = !activeTheme || activeTheme === theme;
          const segments = [
            { key: 'positive', value: pos, color: SENT_COLORS.positive },
            { key: 'neutral', value: neu, color: SENT_COLORS.neutral },
            { key: 'negative', value: neg, color: SENT_COLORS.negative },
          ];
          return (
            <div
              key={theme}
              title={`${theme}: ${count} messages`}
              onClick={() => onThemeClick(theme)}
              style={{
                width: `${widthPct}%`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column-reverse',
                cursor: 'pointer',
                opacity: isActive ? 1 : 0.3,
                transition: 'opacity 0.2s',
                borderRadius: 4,
                overflow: 'hidden',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltip({ theme, count, pos, neu, neg, x: rect.left, y: rect.top });
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              {segments.map(({ key, value, color }) => {
                const pct = count > 0 ? (value / count) * 100 : 0;
                return (
                  <div
                    key={key}
                    style={{
                      width: '100%',
                      height: `${pct}%`,
                      background: color,
                      transition: 'height 0.4s ease',
                    }}
                  />
                );
              })}
              {/* Theme label below bar */}
            </div>
          );
        })}
      </div>

      {/* X-axis labels */}
      <div style={{ display: 'flex', gap: 3, marginTop: 6 }}>
        {themes.map(({ theme, count }) => {
          const widthPct = (count / grandTotal) * 100;
          const isActive = !activeTheme || activeTheme === theme;
          return (
            <div
              key={theme}
              style={{
                width: `${widthPct}%`,
                textAlign: 'center',
                overflow: 'hidden',
              }}
            >
              <span style={{
                fontSize: 10,
                color: isActive ? getThemeColor(theme).color : '#d1d5db',
                fontWeight: 600,
                textTransform: 'capitalize',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                letterSpacing: '0.03em',
              }}>
                {widthPct > 8 ? theme : theme[0].toUpperCase()}
              </span>
              <span style={{
                fontSize: 9,
                color: isActive ? '#9ca3af' : '#e5e7eb',
                display: 'block',
              }}>
                {widthPct > 8 ? count : ''}
              </span>
            </div>
          );
        })}
      </div>

      {/* Floating tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed',
          top: tooltip.y - 100,
          left: tooltip.x,
          background: '#fff',
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          pointerEvents: 'none',
          zIndex: 100,
          minWidth: 130,
        }}>
          <p style={{ fontWeight: 700, textTransform: 'capitalize', marginBottom: 6, color: getThemeColor(tooltip.theme).color }}>
            {tooltip.theme}
          </p>
          <p style={{ color: '#6b7280', marginBottom: 4 }}>Total: <strong style={{ color: '#111' }}>{tooltip.count}</strong></p>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: SENT_COLORS.positive }}>▲{tooltip.pos}</span>
            <span style={{ color: SENT_COLORS.neutral }}>— {tooltip.neu}</span>
            <span style={{ color: SENT_COLORS.negative }}>▼{tooltip.neg}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── THEME LIST PANEL ─────────────────────────────────────────────────────────

const SENTIMENT_SCORE = { positive: 1, neutral: 0, negative: -1 };

function sentimentScore(bd) {
  if (!bd) return 0;
  const total = bd.positive + bd.neutral + bd.negative;
  if (!total) return 0;
  return (bd.positive * 1 + bd.neutral * 0 + bd.negative * -1) / total;
}

function TrafficLight({ score }) {
  // score: 1=positive, 0=neutral, -1=negative (or fractional)
  const color = score > 0.2 ? '#2e8b57' : score < -0.2 ? '#C53030' : '#d4a017';
  const label = score > 0.2 ? 'Positive' : score < -0.2 ? 'Negative' : 'Neutral';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{
        width: 9, height: 9, borderRadius: '50%',
        background: color,
        boxShadow: `0 0 0 2px ${color}30`,
        flexShrink: 0,
      }} />
      <span style={{ fontSize: 11, color, fontWeight: 600 }}>{label}</span>
    </div>
  );
}

function ThemeListPanel({ themeCounts, sentimentBreakdown, sentimentOverTime, activeTheme, onThemeClick }) {
  const [tab, setTab] = useState('themes'); // 'themes' | 'sentiments'

  const items = useMemo(() => {
    if (!themeCounts?.length) return [];
    return themeCounts.map(({ theme, count }) => {
      // Prefer explicit sentimentBreakdown; fall back to averaging sentimentOverTime avgScores
      const bd = sentimentBreakdown?.find(s => s.theme === theme);
      let score = 0;
      if (bd) {
        score = sentimentScore(bd);
      } else if (sentimentOverTime?.length) {
        const rows = sentimentOverTime.filter(r => r._id?.theme === theme);
        if (rows.length) {
          const totalCount = rows.reduce((s, r) => s + (r.count || 1), 0);
          const weightedSum = rows.reduce((s, r) => s + (r.avgScore || 0) * (r.count || 1), 0);
          // avgScore is -2 to +2; normalise to -1 to +1 for TrafficLight
          score = totalCount ? (weightedSum / totalCount) / 2 : 0;
        }
      }
      return { theme, count, score, bd };
    });
  }, [themeCounts, sentimentBreakdown, sentimentOverTime]);

  // Themes tab = alphabetical, Sentiments tab = worst (most negative) first
  const sorted = useMemo(() => {
    const arr = [...items];
    if (tab === 'themes') return arr.sort((a, b) => a.theme.localeCompare(b.theme));
    return arr.sort((a, b) => a.score - b.score);
  }, [items, tab]);

  return (
    <div style={{
      background: 'var(--card, #fff)',
      border: '1px solid var(--border, #e5e7eb)',
      borderRadius: 10,
      display: 'flex',
      flexDirection: 'column',
      height: 360,
      overflow: 'hidden',
    }}>
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border, #e5e7eb)', flexShrink: 0 }}>
        {[
          { key: 'themes', label: 'Themes A–Z' },
          { key: 'sentiments', label: 'Sentiment ↑' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              flex: 1,
              padding: '10px 0',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              border: 'none',
              cursor: 'pointer',
              background: tab === key ? 'var(--card, #fff)' : 'var(--accent, #f9fafb)',
              color: tab === key ? '#395b81' : '#9ca3af',
              borderBottom: tab === key ? '2px solid #395b81' : '2px solid transparent',
              transition: 'all 0.15s',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Column headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr auto',
        alignItems: 'center',
        gap: 8,
        padding: '6px 14px',
        borderBottom: '1px solid var(--border, #e5e7eb)',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af' }}>Theme</span>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af' }}>Sentiment</span>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', textAlign: 'right', minWidth: 40 }}>#</span>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {sorted.length === 0 && (
          <p style={{ fontSize: 13, color: '#9ca3af', padding: '16px 14px' }}>No data yet</p>
        )}
        {sorted.map(({ theme, count, score }) => {
          const tc = getThemeColor(theme);
          const isActive = !activeTheme || activeTheme === theme;

          return (
            <div
              key={theme}
              onClick={() => onThemeClick(theme)}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr auto',
                alignItems: 'center',
                gap: 8,
                padding: '9px 14px',
                borderBottom: '1px solid var(--border, #f4f4f4)',
                cursor: 'pointer',
                opacity: isActive ? 1 : 0.4,
                background: activeTheme === theme ? tc.bg : 'transparent',
                transition: 'background 0.15s, opacity 0.15s',
              }}
              onMouseEnter={e => { if (activeTheme !== theme) e.currentTarget.style.background = 'var(--accent, #f9fafb)'; }}
              onMouseLeave={e => { if (activeTheme !== theme) e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Col 1: Theme name */}
              <span style={{
                fontSize: 13, fontWeight: 600, textTransform: 'capitalize',
                color: 'var(--foreground, #111)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {theme}
              </span>

              {/* Col 2: Sentiment indicator */}
              <TrafficLight score={score} />

              {/* Col 3: Message count */}
              <span style={{
                fontSize: 11, color: '#9ca3af', fontWeight: 600,
                textAlign: 'right', whiteSpace: 'nowrap', minWidth: 40,
              }}>
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── THEME FREQUENCY PANEL (combined Marimekko + list) ────────────────────────

function ThemeFrequencyPanel({ themeCounts, sentimentBreakdown, sentimentOverTime, activeTheme, onThemeClick, loading }) {
  const [chartMode, setChartMode] = useState('marimekko'); // 'marimekko' | 'wordcloud'

  if (loading) {
    return (
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: 2 }}><Skeleton height={360} /></div>
        <div style={{ flex: 1 }}><Skeleton height={360} /></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', height: 360 }}>
      {/* Left 2/3 - chart */}
      <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 0 }}>
        {/* Chart toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          marginBottom: 8,
          gap: 4,
        }}>
          {[
            { key: 'marimekko', label: 'Marimekko' },
            { key: 'wordcloud', label: 'Word cloud' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setChartMode(key)}
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 6,
                border: chartMode === key ? '1px solid #395b81' : '1px solid var(--border, #e5e7eb)',
                background: chartMode === key ? 'rgba(57,91,129,0.08)' : 'transparent',
                color: chartMode === key ? '#395b81' : '#9ca3af',
                cursor: 'pointer',
                transition: 'all 0.15s',
                letterSpacing: '0.03em',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }}>
          {chartMode === 'marimekko'
            ? <MarimekkoChart
                themeCounts={themeCounts}
                sentimentBreakdown={sentimentBreakdown}
                activeTheme={activeTheme}
                onThemeClick={onThemeClick}
              />
            : <WordCloud
                themeCounts={themeCounts}
                activeTheme={activeTheme}
                onThemeClick={onThemeClick}
              />
          }
        </div>
      </div>

      {/* Right 1/3 - list */}
      <div style={{ flex: 1 }}>
        <ThemeListPanel
          themeCounts={themeCounts}
          sentimentBreakdown={sentimentBreakdown}
          sentimentOverTime={sentimentOverTime}
          activeTheme={activeTheme}
          onThemeClick={onThemeClick}
        />
      </div>
    </div>
  );
}

// ─── SENTIMENT CHART ──────────────────────────────────────────────────────────

function SentimentChart({ sentimentOverTime, granularity, activeTheme }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const { labels, datasets } = buildChartData(sentimentOverTime, granularity, activeTheme);

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: datasets.length > 1,
            position: 'bottom',
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              borderRadius: 2,
              useBorderRadius: true,
              font: { size: 12 },
              color: '#6b7280',
              padding: 16,
            },
          },
          tooltip: {
            backgroundColor: '#fff',
            borderColor: 'rgba(0,0,0,0.1)',
            borderWidth: 1,
            titleColor: '#888780',
            bodyColor: '#374151',
            padding: 10,
            boxPadding: 4,
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed.y;
                if (v == null) return null;
                const label = v > 0 ? `+${v}` : `${v}`;
                return ` ${ctx.dataset.label}: ${label}`;
              },
            },
          },
        },
        scales: {
          x: {
            ticks: { color: '#9ca3af', font: { size: 11 }, maxRotation: 45, autoSkip: true, maxTicksLimit: 14 },
            grid: { color: 'rgba(0,0,0,0.05)' },
            border: { display: false },
          },
          y: {
            min: -2,
            max: 2,
            ticks: {
              color: '#9ca3af',
              font: { size: 11 },
              stepSize: 1,
              callback: (v) => {
                const map = { 2: 'Very positive', 1: 'Positive', 0: 'Neutral', '-1': 'Negative', '-2': 'Very negative' };
                return map[v] ?? v;
              },
            },
            grid: { color: 'rgba(0,0,0,0.05)' },
            border: { display: false },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    };
  }, [sentimentOverTime, granularity, activeTheme]);

  const hasData = sentimentOverTime?.length > 0;

  return (
    <Card>
      <div style={{ position: 'relative', height: 260 }}>
        {!hasData && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <p style={{ fontSize: 13, color: '#9ca3af' }}>No sentiment data yet</p>
          </div>
        )}
        <canvas ref={canvasRef} />
      </div>
    </Card>
  );
}

// ─── CHANNEL BARS ─────────────────────────────────────────────────────────────

function ChannelBars({ channelCounts }) {
  if (!channelCounts?.length) return <EmptyState />;
  const sorted = [...channelCounts].sort((a, b) => b.count - a.count);
  const max = sorted[0].count;
  return (
    <div style={{ marginTop: 4 }}>
      {sorted.map(({ channel, count }) => (
        <div key={channel} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13 }}>
          <span style={{ width: 72, color: 'var(--muted-foreground, #6b7280)', textTransform: 'capitalize', flexShrink: 0 }}>
            {channel}
          </span>
          <div style={{ flex: 1, height: 6, background: 'var(--border, #e5e7eb)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.round((count / max) * 100)}%`,
              height: '100%', borderRadius: 3,
              background: CHANNEL_COLORS[channel] || '#888',
              transition: 'width 0.4s ease',
            }} />
          </div>
          <span style={{ width: 30, textAlign: 'right', fontWeight: 600, color: 'var(--foreground, #111)' }}>
            {count}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── GRANULARITY TOGGLE ───────────────────────────────────────────────────────

function GranularityToggle({ value, onChange }) {
  const options = ['hourly', 'daily', 'weekly', 'monthly'];
  return (
    <div style={{
      display: 'flex',
      border: '1px solid var(--border, #e5e7eb)',
      borderRadius: 8, overflow: 'hidden',
    }}>
      {options.map((g, i) => {
        const isActive = value === g;
        return (
          <button
            key={g}
            onClick={() => onChange(g)}
            style={{
              background: isActive ? '#f0856d' : 'transparent',
              border: 'none',
              borderLeft: i !== 0 ? '1px solid var(--border, #e5e7eb)' : 'none',
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: isActive ? 700 : 500,
              cursor: 'pointer',
              color: isActive ? '#fff' : 'var(--muted-foreground, #6b7280)',
              transition: 'all 0.15s',
              textTransform: 'capitalize',
              letterSpacing: isActive ? '0.01em' : 'normal',
            }}
          >
            {g}
          </button>
        );
      })}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function Overview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [granularity, setGranularity] = useState('weekly');
  const [activeTheme, setActiveTheme] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchStats = useCallback(async (gran) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ granularity: gran });
      const res = await fetch(`${API_URL}/api/overview/stats?${params}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      setData(json.data);
      setError(null);
    } catch (err) {
      console.error('Overview fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(granularity);
  }, [fetchStats, granularity]);

  const handleThemeClick = useCallback((theme) => {
    setActiveTheme(prev => prev === theme ? null : theme);
  }, []);

  const totalMessages = data?.statusCounts?.reduce((s, c) => s + c.count, 0) || 0;

  return (
    <DashboardLayout>
      <LoadingBar isLoading={loading} />

      {/* ── PAGE HEADER ── */}
      <div style={{
        marginBottom: 28,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap'
      }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--foreground)', marginBottom: 4 }}>
            Overview
          </h1>
          <p style={{ fontSize: 14, color: 'var(--muted-foreground, #6b7280)' }}>
            Message themes, sentiment trends, and team performance
          </p>
        </div>

        {user?.role === 'superadmin' && (
          <button
            onClick={() => setInviteOpen(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
              color: '#395b81',
              background: 'rgba(57,91,129,0.08)',
              border: '1px solid rgba(57,91,129,0.25)',
              borderRadius: 6,
              padding: '8px 14px',
              cursor: 'pointer',
              transition: 'background 0.15s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(57,91,129,0.14)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(57,91,129,0.08)'}
          >
            <UserPlus size={15} />
            Invite admin
          </button>
        )}
      </div>

      {/* ── ERROR BANNER ── */}
      {error && (
        <div style={{
          background: 'rgba(240,133,109,0.08)', border: '1px solid rgba(240,133,109,0.3)', borderRadius: 8,
          padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#c06a55',
        }}>
          Failed to load overview data: {error}
        </div>
      )}

      {/* ── THEME FREQUENCY ── */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <SectionLabel>Theme frequency — click to filter</SectionLabel>
          {activeTheme && (
            <span
              onClick={() => setActiveTheme(null)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 12, fontWeight: 600, padding: '3px 10px',
                borderRadius: 999, cursor: 'pointer',
                background: getThemeColor(activeTheme).bg,
                color: getThemeColor(activeTheme).color,
                border: `1px solid ${getThemeColor(activeTheme).border}`,
              }}
            >
              {activeTheme} <span style={{ fontSize: 15, lineHeight: 1 }}>×</span>
            </span>
          )}
        </div>
        <ThemeFrequencyPanel
          themeCounts={data?.themeCounts}
          sentimentBreakdown={data?.sentimentBreakdown}
          sentimentOverTime={data?.sentimentOverTime}
          activeTheme={activeTheme}
          onThemeClick={handleThemeClick}
          loading={loading}
        />
      </div>

      {/* ── SENTIMENT CHART ── */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <SectionLabel>
            Sentiment over time{activeTheme ? ` · ${activeTheme}` : ''}
          </SectionLabel>
          <GranularityToggle value={granularity} onChange={setGranularity} />
        </div>
        {loading
          ? <Skeleton height={290} />
          : <SentimentChart sentimentOverTime={data?.sentimentOverTime} granularity={granularity} activeTheme={activeTheme} />
        }
      </div>

      {/* ── MANAGEMENT STATS ── */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ marginBottom: 12 }}>
          <SectionLabel>Management stats</SectionLabel>
        </div>
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px,1fr))', gap: 12 }}>
            {[1, 2, 3, 4].map(i => <Skeleton key={i} height={160} />)}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px,1fr))', gap: 12 }}>

            {/* Messages per admin */}
            <Card>
              <CardTitle>Messages per admin</CardTitle>
              {data?.adminStats?.length
                ? data.adminStats.map(({ admin, messageCount }) => (
                  <StatRow key={admin} label={admin} value={messageCount} />
                ))
                : <EmptyState message="No admin data yet" />
              }
            </Card>

            {/* Avg response time */}
            <Card>
              <CardTitle>Avg response time</CardTitle>
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 26, fontWeight: 600, lineHeight: 1, color: 'var(--foreground)' }}>
                  {formatResponseTime(data?.globalAvgResponseMins)}
                </p>
                <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                  across {data?.totalReplied ?? 0} replies
                </p>
              </div>
              {data?.adminStats?.filter(a => a.avgResponseMins != null).map(({ admin, avgResponseMins }) => (
                <StatRow key={admin} label={admin} value={formatResponseTime(avgResponseMins)} />
              ))}
            </Card>

            {/* Messages per channel */}
            <Card>
              <CardTitle>Messages per channel</CardTitle>
              <ChannelBars channelCounts={data?.channelCounts} />
            </Card>

            {/* Message status */}
            <Card>
              <CardTitle>Message status</CardTitle>
              {data?.statusCounts?.length ? (
                <>
                  {[...data.statusCounts]
                    .sort((a, b) => b.count - a.count)
                    .map(({ status, count }) => (
                      <StatRow
                        key={status}
                        label={status.charAt(0).toUpperCase() + status.slice(1)}
                        value={count}
                        valueColor={STATUS_COLORS[status]}
                      />
                    ))
                  }
                  <StatRow label="Total" value={totalMessages} />
                </>
              ) : (
                <EmptyState />
              )}
            </Card>

            {/* Message processing errors */}
            <Card>
              <CardTitle>Message errors</CardTitle>
              {data?.errorCount > 0 ? (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <p style={{ fontSize: 26, fontWeight: 600, lineHeight: 1, color: '#c06a55' }}>
                      {data.errorCount}
                    </p>
                    <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                      message{data.errorCount !== 1 ? 's' : ''} with processing errors
                    </p>
                  </div>
                  {(data?.errorBreakdown || []).filter(e => e.step !== 'reply').map(({ step, count }) => (
                    <StatRow
                      key={step}
                      label={step.charAt(0).toUpperCase() + step.slice(1).replace(/_/g, ' ')}
                      value={count}
                      valueColor="#c06a55"
                    />
                  ))}
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border, #f0f0f0)' }}>
                    <button
                      onClick={() => navigate('/messages', { state: { filter: 'errors' } })}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#c06a55', background: 'rgba(240,133,109,0.1)', border: '1px solid rgba(240,133,109,0.3)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}
                    >
                      View affected messages →
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0', gap: 6 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(84,122,132,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 16 }}>✓</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#547a84', fontWeight: 600, margin: 0 }}>No errors</p>
                  <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>All messages processed cleanly</p>
                </div>
              )}
            </Card>

          </div>
        )}
      </div>

      <InviteAdminModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />

    </DashboardLayout>
  );
}